import { clientLogger } from '@/lib/clientLogger';
import { useRef, useState } from 'react';
import { apiService } from '@/lib/api';
import { ContactLookupResultDTO } from '@/types';

type LookupState = {
  phoneResults: ContactLookupResultDTO[];
  emailResults: ContactLookupResultDTO[];
  isLoading: boolean;
};

const DEFAULT_DEBOUNCE_MS = 400;

export function useContactLookup(companyId?: string, debounceMs: number = DEFAULT_DEBOUNCE_MS) {
  const [state, setState] = useState<LookupState>({
    phoneResults: [],
    emailResults: [],
    isLoading: false,
  });

  const phoneTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const emailTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = () => {
    if (phoneTimer.current) {
      clearTimeout(phoneTimer.current);
      phoneTimer.current = null;
    }
    if (emailTimer.current) {
      clearTimeout(emailTimer.current);
      emailTimer.current = null;
    }
  };

  const lookupPhone = (phoneRaw: string) => {
    const phone = phoneRaw.trim();

    if (!companyId) {
      setState((prev) => ({ ...prev, phoneResults: [], isLoading: false }));
      if (phoneTimer.current) clearTimeout(phoneTimer.current);
      return;
    }

    if (phone.length < 7) {
      setState((prev) => ({ ...prev, phoneResults: [], isLoading: false }));
      if (phoneTimer.current) clearTimeout(phoneTimer.current);
      return;
    }

    if (phoneTimer.current) clearTimeout(phoneTimer.current);
    phoneTimer.current = setTimeout(async () => {
      setState((prev) => ({ ...prev, isLoading: true }));
      try {
        const results = await apiService.lookupContactByPhone(phone);
        setState((prev) => ({ ...prev, phoneResults: results, isLoading: false }));
      } catch (error) {
        clientLogger.error('Failed to lookup contact by phone', error);
        setState((prev) => ({ ...prev, phoneResults: [], isLoading: false }));
      }
    }, debounceMs);
  };

  const lookupEmail = (emailRaw: string) => {
    const email = emailRaw.trim();

    if (!companyId) {
      setState((prev) => ({ ...prev, emailResults: [], isLoading: false }));
      if (emailTimer.current) clearTimeout(emailTimer.current);
      return;
    }
    const atIndex = email.indexOf('@');
    const dotAfterAt = email.indexOf('.', atIndex + 1);

    if (atIndex <= 0 || dotAfterAt <= atIndex + 1) {
      setState((prev) => ({ ...prev, emailResults: [], isLoading: false }));
      if (emailTimer.current) clearTimeout(emailTimer.current);
      return;
    }

    if (emailTimer.current) clearTimeout(emailTimer.current);
    emailTimer.current = setTimeout(async () => {
      setState((prev) => ({ ...prev, isLoading: true }));
      try {
        const results = await apiService.lookupContactByEmail(email);
        setState((prev) => ({ ...prev, emailResults: results, isLoading: false }));
      } catch (error) {
        clientLogger.error('Failed to lookup contact by email', error);
        setState((prev) => ({ ...prev, emailResults: [], isLoading: false }));
      }
    }, debounceMs);
  };

  const clearResults = () => {
    clearTimers();
    setState({ phoneResults: [], emailResults: [], isLoading: false });
  };

  return {
    phoneResults: state.phoneResults,
    emailResults: state.emailResults,
    isLoading: state.isLoading,
    lookupPhone,
    lookupEmail,
    clearResults,
  };
}


